import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminOrg, getMembers, rotateJoinCode, getAllOrgs } from '../auth/authApi';
import ConsoleShell from '../components/ConsoleShell';
import Spinner from '../components/Spinner';
import { ADMIN_ROLES } from '../lib/consoleUtils';

const SU_BAR_H = 52;

const bootScreen = (content) => (
  <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', fontFamily: 'system-ui', color: '#5E5947', background: '#F0EDE6' }}>{content}</div>
);

const suBar = {
  position: 'sticky', top: 0, zIndex: 30, height: SU_BAR_H, boxSizing: 'border-box',
  background: '#0A0A0A', color: '#fff', display: 'flex', alignItems: 'center', gap: 12,
  padding: '0 20px', fontFamily: 'system-ui, sans-serif', fontSize: 13,
  borderBottom: '1px solid #262626',
};
const suSelect = {
  background: '#1A1A1A', color: '#fff', border: '1px solid #3A3A3A', borderRadius: 7,
  padding: '6px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', outline: 'none',
};

// Persistent console chrome. For a superadmin, a black super-user bar sits on top
// with an org switcher; the console below re-fetches + re-themes to the selected
// org (default: Range Drills / the platform org).
export default function ConsoleLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSuper = !!(user && user.role === 'superadmin');
  const isAdmin = !!(user && ADMIN_ROLES.includes(user.role));

  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (user && !isAdmin) navigate('/portal', { replace: true }); }, [user, isAdmin, navigate]);

  // Superadmin: load all orgs, default to Range Drills (platform).
  useEffect(() => {
    if (!isSuper) return;
    let alive = true;
    getAllOrgs()
      .then((list) => {
        if (!alive) return;
        setOrgs(list);
        const def = list.find((o) => o.type === 'platform') || list[0];
        setSelectedOrgId(def ? def.id : null);
      })
      .catch(() => { if (alive) setSelectedOrgId(null); }); // fall back to own org
    return () => { alive = false; };
  }, [isSuper]);

  // Load the target org (own org for a regular admin; the selected org for a super).
  const targetOrgId = isSuper ? selectedOrgId : undefined;
  useEffect(() => {
    if (isSuper && !selectedOrgId) return; // wait for the org list to pick a default
    let alive = true;
    setError('');
    Promise.all([getAdminOrg(targetOrgId), getMembers(targetOrgId)])
      .then(([o, m]) => { if (alive) { setOrg(o); setMembers(m); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [targetOrgId, isSuper, selectedOrgId]);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(org.joinCode); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* blocked */ }
  };
  const rotate = async () => {
    if (!window.confirm('Generate a new join code? The current code will stop working immediately.')) return;
    try { const { joinCode } = await rotateJoinCode(targetOrgId); setOrg((o) => ({ ...o, joinCode })); } catch (e) { window.alert('Could not rotate the code. Please try again.'); }
  };

  if (loading || !org) {
    if (error === 'forbidden') return bootScreen('You don’t have access to this console.');
    if (error) return bootScreen('Couldn’t load the console. Please try again.');
    return bootScreen(<div style={{ display: 'grid', placeItems: 'center', gap: 14 }}><Spinner size={34} /><span>Loading console…</span></div>);
  }

  const active = location.pathname.startsWith('/console/catalog') ? 'catalog' : 'overview';
  const suName = (user && (user.fullName || user.email)) || 'Superadmin';

  const joinBox = (
    <div className="rail-code">
      <div className="rc-label">Join code</div>
      <div className="rc-code">{org.joinCode || '—'}</div>
      <div className="rc-actions">
        <button className="rc-btn" onClick={copyCode}>{copied ? 'Copied ✓' : 'Copy'}</button>
        <button className="rc-btn primary" onClick={rotate}>Rotate</button>
      </div>
    </div>
  );

  return (
    <>
      {isSuper && (
        <div style={suBar}>
          <span style={{ fontWeight: 700 }}>Super user</span>
          <span style={{ opacity: 0.7 }}>{suName} · {user.email}</span>
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ opacity: 0.7 }}>Viewing</span>
            <select value={selectedOrgId || ''} onChange={(e) => setSelectedOrgId(e.target.value)} style={suSelect}>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name} · {o.memberCount} member{o.memberCount === 1 ? '' : 's'}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <ConsoleShell org={org} memberCount={members.length} active={active} railWidget={joinBox} topOffset={isSuper ? SU_BAR_H : 0}>
        <Outlet context={{ org, members, orgId: targetOrgId }} />
      </ConsoleShell>
    </>
  );
}
